import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { normalizeRole } from "../utils/rolePermissions";
import "./Smartscanoverview.css";
const API_HOST =
  process.env.REACT_APP_API_URL ||
  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net";

const API_BASE = API_HOST + "/api";

const RESTRICTED_ROLES = ["seller", "vendor"];
const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "#92400e", bg: "#fef3c7" },
  uploaded: { label: "Uploaded", color: "#065f46", bg: "#d1fae5" },
  rejected: { label: "Rejected", color: "#991b1b", bg: "#fee2e2" },
};

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "uploaded", label: "Uploaded" },
  { key: "rejected", label: "Rejected" },
];

const PRICING_TABS = [
  { key: "", label: "All" },
  { key: "list", label: "Listed" },
  { key: "reject", label: "Rejected" },
  { key: "no_data", label: "No Data" },
];

const StatusPill = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || {
    label: status || "Unknown",
    color: "#374151",
    bg: "#f3f4f6",
  };
  return (
    <span
      className="sso-status-pill"
      style={{ color: cfg.color, background: cfg.bg }}
    >
      {cfg.label}
    </span>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ── Skeleton placeholder row, shown while the first page loads ──────
const SkeletonRow = () => (
  <tr className="sso-row sso-row--skeleton">
    <td className="sso-cell-image">
      <div className="sso-thumb sso-skeleton-block" />
    </td>
    <td>
      <div className="sso-skeleton-line" style={{ width: "70%" }} />
      <div
        className="sso-skeleton-line"
        style={{ width: "40%", marginTop: 6 }}
      />
    </td>
    <td>
      <div className="sso-skeleton-line" style={{ width: "60px" }} />
    </td>
    <td>
      <div className="sso-skeleton-line" style={{ width: "60px" }} />
    </td>
    <td>
      <div className="sso-skeleton-line" style={{ width: "60px" }} />
    </td>
    <td>
      <div className="sso-skeleton-line" style={{ width: "80px" }} />
    </td>
    <td>
      <div className="sso-skeleton-line" style={{ width: "70px" }} />
    </td>
    <td>
      <div className="sso-skeleton-line" style={{ width: "70px" }} />
    </td>
    <td></td>
  </tr>
);

// ── Memoized table row —- only re-renders when its own props change,
//    not when unrelated rows/state elsewhere on the page change ────
const ScanRow = memo(function ScanRow({
  row,
  isRestricted,
  isExpanded,
  expandedDetail,
  expandLoading,
  onToggleExpand,
  onOpenPricing,
}) {
  return (
    <React.Fragment>
      <tr className="sso-row">
        <td className="sso-cell-image" data-label="Image">
          {row.primary_image ? (
            <img
              src={row.primary_image}
              alt={row.title}
              className="sso-thumb"
              width="48"
              height="48"
              loading="lazy"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/60";
              }}
            />
          ) : (
            <div className="sso-thumb sso-thumb--empty" />
          )}
        </td>
        <td className="sso-cell-title" data-label="Title">
          <div className="sso-title-text">{row.title}</div>
          <div className="sso-subtext">
            {row.brand} {row.size ? `• ${row.size}` : ""}
          </div>
        </td>
        <td className="sso-cell-price" data-label="AI Price">
          {row.currency}
          {row.price_recommended ?? "—"}
        </td>
        {!isRestricted && (
          <td className="sso-cell-price" data-label="Pricing">
            {row.fleek_decision === "LIST" ? (
              <span className="sso-to-price">
                {row.currency}
                {row.fleek_list_price}
              </span>
            ) : (
              <span className="sso-to-price sso-to-price--none">—</span>
            )}
          </td>
        )}
        {!isRestricted && (
          <td className="sso-cell-price" data-label="TO Margin">
            {row.fleek_decision === "LIST"
              ? `${row.currency}${row.fleek_to_margin}`
              : "—"}
          </td>
        )}
        <td className="sso-cell-seller" data-label="Seller">
          {row.seller_name || "Unknown"}
        </td>
        <td className="sso-cell-status" data-label="Status">
          <StatusPill status={row.status} />
          {row.status === "rejected" && row.rejection_reason && (
            <span className="sso-rejection-tag">{row.rejection_reason}</span>
          )}
        </td>
        <td className="sso-cell-date" data-label="Date">
          {formatDate(row.created_at)}
        </td>
        <td className="sso-cell-expand">
          <div className="sso-action-btns">
            {!isRestricted && (
              <button
                type="button"
                className="sso-pricing-btn"
                onClick={() => onOpenPricing(row)}
                disabled={!row.fleek_decision}
                title={
                  row.fleek_decision
                    ? "View full pricing breakdown"
                    : "No pricing data for this item"
                }
              >
                Pricing Module
              </button>
            )}
            <button
              type="button"
              className={`sso-expand-btn ${isExpanded ? "sso-expand-btn--open" : ""}`}
              onClick={() => onToggleExpand(row)}
            >
              {isExpanded ? "Hide" : "Expand"}
            </button>
          </div>
        </td>
      </tr>

      {isExpanded && (
        <tr className="sso-expand-row">
          <td colSpan={isRestricted ? 6 : 8}>
            {expandLoading ? (
              <div className="sso-expand-loading">Loading details...</div>
            ) : expandedDetail ? (
              <div className="sso-expand-content">
                <div className="sso-expand-images">
                  {(expandedDetail.images || []).map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt={`${expandedDetail.title} ${i + 1}`}
                      className="sso-expand-image"
                      width="100"
                      height="100"
                      loading="lazy"
                    />
                  ))}
                  {(!expandedDetail.images ||
                    expandedDetail.images.length === 0) && (
                    <span className="sso-subtext">No images available.</span>
                  )}
                </div>

                <div className="sso-expand-details">
                  <div className="sso-detail-grid">
                    <div className="sso-detail-item">
                      <span className="sso-detail-label">SKU</span>
                      <span>{expandedDetail.sku || "—"}</span>
                    </div>
                    <div className="sso-detail-item">
                      <span className="sso-detail-label">Color</span>
                      <span>{expandedDetail.color || "—"}</span>
                    </div>
                    <div className="sso-detail-item">
                      <span className="sso-detail-label">Material</span>
                      <span>{expandedDetail.material || "—"}</span>
                    </div>
                    <div className="sso-detail-item">
                      <span className="sso-detail-label">Category</span>
                      <span>
                        {[
                          expandedDetail.l1_gender,
                          expandedDetail.l2_type,
                          expandedDetail.l3_category,
                          expandedDetail.l4_subcategory,
                        ]
                          .filter(Boolean)
                          .join(" > ") || "—"}
                      </span>
                    </div>
                    <div className="sso-detail-item">
                      <span className="sso-detail-label">Condition</span>
                      <span>{expandedDetail.condition_score ?? "—"}/10</span>
                    </div>
                    <div className="sso-detail-item">
                      <span className="sso-detail-label">Authenticity</span>
                      <span>{expandedDetail.authenticity_score ?? "—"}%</span>
                    </div>
                  </div>

                  {expandedDetail.description && (
                    <div className="sso-detail-block">
                      <span className="sso-detail-label">Description</span>
                      <p className="sso-detail-text">
                        {expandedDetail.description}
                      </p>
                    </div>
                  )}

                  {expandedDetail.condition_summary && (
                    <div className="sso-detail-block">
                      <span className="sso-detail-label">Condition Notes</span>
                      <p className="sso-detail-text">
                        {expandedDetail.condition_summary}
                      </p>
                    </div>
                  )}

                  {expandedDetail.authenticity_summary && (
                    <div className="sso-detail-block">
                      <span className="sso-detail-label">
                        Authenticity Notes
                      </span>
                      <p className="sso-detail-text">
                        {expandedDetail.authenticity_summary}
                      </p>
                    </div>
                  )}

                  {expandedDetail.seo_tags && (
                    <div className="sso-detail-block">
                      <span className="sso-detail-label">Tags</span>
                      <div className="sso-tags">
                        {expandedDetail.seo_tags
                          .split(",")
                          .map((t) => t.trim())
                          .filter(Boolean)
                          .map((t, i) => (
                            <span key={i} className="sso-tag">
                              {t}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="sso-expand-loading">No details found.</div>
            )}
          </td>
        </tr>
      )}
    </React.Fragment>
  );
});

const SmartScanOverview = () => {
  const { user, companyId } = useAuth();
  const userRole = normalizeRole(user?.role);
  const isRestricted = RESTRICTED_ROLES.includes(userRole);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);

  // ── Pagination ───────────────────────────────────────────
  const [page, setPage] = useState(1); // 1-indexed
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // ── Search + filters ─────────────────────────────────────
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pricingFilter, setPricingFilter] = useState(""); // "", "list", "reject", "no_data"
  const searchTimerRef = useRef(null);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchInput]);

  // ── Expand state ────────────────────────────────────────
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [expandLoading, setExpandLoading] = useState(false);

  // ── Pricing Module modal state ─────────────────────────
  const [pricingModalRow, setPricingModalRow] = useState(null);
  const [pricingModalLoading, setPricingModalLoading] = useState(false);

  const fetchPage = useCallback(
    async (pageNum) => {
      if (!companyId) return;
      setLoading(true);
      setError(null);

      try {
        const res = await axios.get(`${API_BASE}/smartscan/overview`, {
          params: {
            company_id: companyId,
            role: userRole,
            search: debouncedSearch || undefined,
            status: statusFilter || undefined,
            pricing_status: pricingFilter || undefined,
            limit: PAGE_SIZE,
            offset: (pageNum - 1) * PAGE_SIZE,
          },
        });
        if (res.data.success) {
          setRows(res.data.data);
          setTotal(res.data.total ?? 0);
        }
      } catch (err) {
        console.error("[SmartScanOverview] Failed to load overview:", err);
        setError("Failed to load SmartScan overview. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [companyId, userRole, debouncedSearch, statusFilter, pricingFilter],
  );

  // Reset to page 1 whenever any filter changes
  useEffect(() => {
    setPage(1);
    fetchPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, userRole, debouncedSearch, statusFilter, pricingFilter]);

  const goToPage = (p) => {
    const clamped = Math.max(1, Math.min(p, totalPages));
    if (clamped === page) return;
    setPage(clamped);
    fetchPage(clamped);
    // Scroll the table into view on page change — helps on mobile where
    // the table can be long and "page 2" would otherwise land you
    // visually in the middle of nowhere.
    document
      .querySelector(".sso-table-wrapper")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Windowed page numbers: 1 ... p-1 p [p+1] p+2 ... last
  const getPageNumbers = () => {
    const delta = 1;
    const range = [];
    const rangeWithDots = [];
    let last;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= page - delta && i <= page + delta)
      ) {
        range.push(i);
      }
    }

    for (const i of range) {
      if (last) {
        if (i - last === 2) {
          rangeWithDots.push(last + 1);
        } else if (i - last > 2) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      last = i;
    }

    return rangeWithDots;
  };

  const toggleExpand = useCallback(
    async (row) => {
      if (expandedId === row.id) {
        setExpandedId(null);
        setExpandedDetail(null);
        return;
      }

      setExpandedId(row.id);
      setExpandedDetail(null);
      setExpandLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE}/smartscan/overview/${row.id}`,
          { params: { role: userRole } },
        );
        if (res.data.success) {
          setExpandedDetail(res.data.data);
        }
      } catch (err) {
        console.error("[SmartScanOverview] Failed to load detail:", err);
      } finally {
        setExpandLoading(false);
      }
    },
    [expandedId, userRole],
  );

  const openPricingModal = useCallback(
    async (row) => {
      setPricingModalRow({ id: row.id, title: row.title });
      setPricingModalLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE}/smartscan/overview/${row.id}`,
          { params: { role: userRole } },
        );
        if (res.data.success) {
          setPricingModalRow(res.data.data);
        }
      } catch (err) {
        console.error(
          "[SmartScanOverview] Failed to load pricing module:",
          err,
        );
      } finally {
        setPricingModalLoading(false);
      }
    },
    [userRole],
  );

  const closePricingModal = () => setPricingModalRow(null);

  const hasActiveFilters = searchInput || statusFilter || pricingFilter;
  const clearFilters = () => {
    setSearchInput("");
    setStatusFilter("");
    setPricingFilter("");
  };

  return (
    <div className="sso-page">
      <div className="sso-container">
        <div className="sso-header">
          <h1 className="sso-title">SmartScan Overview</h1>
          <span className="sso-count">
            {loading ? "…" : `${total} item${total !== 1 ? "s" : ""}`}
          </span>
        </div>

        {/* ── Search + status filter bar ──────────────────── */}
        <div className="sso-filter-bar">
          <div className="sso-search-wrap">
            <input
              type="text"
              className="sso-search-input"
              placeholder="Search by title, brand, or SKU..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button
                type="button"
                className="sso-search-clear"
                onClick={() => setSearchInput("")}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <div className="sso-status-tabs" role="tablist">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key || "all"}
                type="button"
                role="tab"
                aria-selected={statusFilter === tab.key}
                className={`sso-status-tab ${statusFilter === tab.key ? "sso-status-tab--active" : ""}`}
                onClick={() => setStatusFilter(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              className="sso-clear-filters-btn"
              onClick={clearFilters}
            >
              Clear
            </button>
          )}
        </div>

        {/* ── Pricing Module filter row — hidden from restricted roles,
             same as the Pricing/TO Margin columns themselves ────── */}
        {!isRestricted && (
          <div className="sso-filter-bar sso-filter-bar--pricing">
            <span className="sso-pricing-filter-label">Pricing Module:</span>
            <div className="sso-status-tabs" role="tablist">
              {PRICING_TABS.map((tab) => (
                <button
                  key={tab.key || "all"}
                  type="button"
                  role="tab"
                  aria-selected={pricingFilter === tab.key}
                  className={`sso-status-tab ${pricingFilter === tab.key ? "sso-status-tab--active" : ""}`}
                  onClick={() => setPricingFilter(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && <div className="sso-error-banner">{error}</div>}

        {!loading && rows.length === 0 ? (
          <div className="sso-empty">
            {hasActiveFilters
              ? "No items match your search/filter."
              : "No SmartScan items found."}
          </div>
        ) : (
          <div className="sso-table-wrapper">
            <table className="sso-table">
              <thead>
                <tr>
                  <th className="sso-th-image"></th>
                  <th>Title</th>
                  <th>AI Price</th>
                  {!isRestricted && <th>Pricing</th>}
                  {!isRestricted && <th>TO Margin</th>}
                  <th>Seller</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <SkeletonRow key={`skeleton-${i}`} />
                    ))
                  : rows.map((row) => (
                      <ScanRow
                        key={row.id}
                        row={row}
                        isRestricted={isRestricted}
                        isExpanded={expandedId === row.id}
                        expandedDetail={
                          expandedId === row.id ? expandedDetail : null
                        }
                        expandLoading={
                          expandedId === row.id ? expandLoading : false
                        }
                        onToggleExpand={toggleExpand}
                        onOpenPricing={openPricingModal}
                      />
                    ))}
              </tbody>
            </table>

            {!loading && totalPages > 1 && (
              <div className="sso-pagination">
                <button
                  type="button"
                  className="sso-page-btn sso-page-btn--nav"
                  onClick={() => goToPage(page - 1)}
                  disabled={page === 1}
                >
                  ‹ Prev
                </button>

                {getPageNumbers().map((p, i) =>
                  p === "..." ? (
                    <span key={`dots-${i}`} className="sso-page-dots">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      className={`sso-page-btn ${p === page ? "sso-page-btn--active" : ""}`}
                      onClick={() => goToPage(p)}
                    >
                      {p}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  className="sso-page-btn sso-page-btn--nav"
                  onClick={() => goToPage(page + 1)}
                  disabled={page === totalPages}
                >
                  Next ›
                </button>

                <span className="sso-page-info">
                  Page {page} of {totalPages}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Pricing Module modal ─────────────────────────── */}
      {pricingModalRow && (
        <div className="sso-modal-overlay" onClick={closePricingModal}>
          <div
            className="sso-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sso-modal-header">
              <div>
                <h2 className="sso-modal-title">Pricing Module</h2>
                <p className="sso-modal-subtitle">{pricingModalRow.title}</p>
              </div>
              <button
                type="button"
                className="sso-modal-close"
                onClick={closePricingModal}
              >
                ✕
              </button>
            </div>

            <div className="sso-modal-body">
              {pricingModalLoading ? (
                <div className="sso-expand-loading">
                  Loading pricing breakdown...
                </div>
              ) : pricingModalRow.fleek_decision ? (
                <>
                  <div
                    className={`sso-pricing-decision sso-pricing-decision--${pricingModalRow.fleek_decision.toLowerCase()}`}
                  >
                    {pricingModalRow.fleek_decision === "LIST"
                      ? "✅ Approved to List"
                      : "❌ Rejected on Price"}
                  </div>

                  <div className="sso-pricing-grid">
                    <div className="sso-pricing-row">
                      <span className="sso-pricing-label">Listing Price</span>
                      <span className="sso-pricing-value">
                        {pricingModalRow.currency}
                        {pricingModalRow.fleek_market_price ?? "—"}
                      </span>
                    </div>
                    <div className="sso-pricing-row">
                      <span className="sso-pricing-label">Cost Floor</span>
                      <span className="sso-pricing-value">
                        {pricingModalRow.currency}
                        {pricingModalRow.fleek_cost_floor ?? "—"}
                      </span>
                    </div>
                    {pricingModalRow.fleek_decision === "LIST" && (
                      <>
                        <div className="sso-pricing-row">
                          <span className="sso-pricing-label">
                            Pricing Module Price
                          </span>
                          <span className="sso-pricing-value sso-pricing-value--highlight">
                            {pricingModalRow.currency}
                            {pricingModalRow.fleek_list_price ?? "—"}
                          </span>
                        </div>
                        <div className="sso-pricing-row">
                          <span className="sso-pricing-label">TO Margin</span>
                          <span className="sso-pricing-value">
                            {pricingModalRow.currency}
                            {pricingModalRow.fleek_to_margin ?? "—"}
                          </span>
                        </div>
                        <div className="sso-pricing-row">
                          <span className="sso-pricing-label">
                            Reseller Cut
                          </span>
                          <span className="sso-pricing-value">
                            {pricingModalRow.currency}
                            {pricingModalRow.fleek_reseller_cut ?? "—"}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="sso-pricing-row">
                      <span className="sso-pricing-label">Match Level</span>
                      <span className="sso-pricing-value">
                        {pricingModalRow.fleek_match_level || "—"}
                      </span>
                    </div>
                    <div className="sso-pricing-row">
                      <span className="sso-pricing-label">Confidence</span>
                      <span className="sso-pricing-value">
                        {pricingModalRow.fleek_confidence || "—"}
                      </span>
                    </div>
                  </div>

                  {pricingModalRow.fleek_reason && (
                    <div className="sso-pricing-reason">
                      <span className="sso-detail-label">Reason</span>
                      <p className="sso-detail-text">
                        {pricingModalRow.fleek_reason}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="sso-expand-loading">
                  No pricing data available for this item — TO Price couldn't
                  find enough comparable products to price it against.
                </div>
              )}
            </div>

            <div className="sso-modal-footer">
              <button
                type="button"
                className="sso-modal-close-btn"
                onClick={closePricingModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartScanOverview;
